import { KEYWORDS, CONSTANTS, FUNCTIONS, TOKEN_COLORS } from './constants';

// Lexer for the balance editor's syntax-highlighted overlay. Deliberately
// looser than core/pathway/equation_parser.py — it only has to colour text,
// so it never rejects input and has no notion of statements.

export function tokenize(text) {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i];

    if (c === '\n') {
      tokens.push({ type: 'newline', value: '\n' });
      i++;
      continue;
    }
    if (c === ' ' || c === '\t') {
      let j = i;
      while (j < text.length && (text[j] === ' ' || text[j] === '\t')) j++;
      tokens.push({ type: 'whitespace', value: text.slice(i, j) });
      i = j;
      continue;
    }
    if (c === '#') {
      let j = i;
      while (j < text.length && text[j] !== '\n') j++;
      tokens.push({ type: 'comment', value: text.slice(i, j) });
      i = j;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      while (j < text.length && text[j] !== '"') {
        if (text[j] === '\\' && j + 1 < text.length) j++;
        j++;
      }
      if (j < text.length) j++; // closing quote
      tokens.push({ type: 'string', value: text.slice(i, j) });
      i = j;
      continue;
    }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(text[i + 1] || ''))) {
      let j = i;
      while (j < text.length && /[0-9.]/.test(text[j])) j++;
      // Optional scientific notation
      if (j < text.length && (text[j] === 'e' || text[j] === 'E')) {
        j++;
        if (j < text.length && (text[j] === '+' || text[j] === '-')) j++;
        while (j < text.length && /[0-9]/.test(text[j])) j++;
      }
      tokens.push({ type: 'number', value: text.slice(i, j) });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < text.length && /[A-Za-z0-9_]/.test(text[j])) j++;
      tokens.push({ type: 'identifier', value: text.slice(i, j) });
      i = j;
      continue;
    }
    tokens.push({ type: 'operator', value: c });
    i++;
  }
  return tokens;
}

export function classifyIdentifier(value, knownSlugs) {
  if (KEYWORDS.has(value)) return 'keyword';
  if (CONSTANTS.has(value)) return 'constant';
  if (FUNCTIONS.has(value)) return 'function';
  if (knownSlugs.outputs.includes(value)) return 'output';
  if (knownSlugs.inputs.includes(value)) return 'input';
  if (knownSlugs.params.includes(value)) return 'param';
  return 'identifier';
}

export function tokenColorClass(type) {
  return TOKEN_COLORS[type] ?? '';
}

// Walk lines before the cursor to find `<name> =` patterns that aren't
// already declared as slugs/funcs/constants — those are local let-bindings
// the user has introduced.
export function extractLetBindings(textBefore, knownSlugs) {
  const reserved = new Set([
    ...knownSlugs.inputs,
    ...knownSlugs.outputs,
    ...knownSlugs.params,
    ...FUNCTIONS,
    ...CONSTANTS,
    ...KEYWORDS,
  ]);
  const lets = new Set();
  for (const line of textBefore.split('\n')) {
    const m = line.match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*=/);
    if (m && !reserved.has(m[1])) lets.add(m[1]);
  }
  return Array.from(lets);
}
